import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { createInvoiceForAppointment } from '@principle-theorem/principle-core';
import {
  type IAppointment,
  isAppointment,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  isWithRef,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { type Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ManagementResource } from '../models/management-resource';
import {
  type IManagementTool,
  type IToolRunContext,
} from '../models/management-tool';

function isAppointmentWithRef(value: unknown): value is WithRef<IAppointment> {
  return isWithRef(value) && isAppointment(value) && 'eventHistory' in value;
}

/**
 * Deliberately does NOT apply `Appointment.hasUnlinkedMigratedInvoice`. That
 * guard suppresses *automatic* invoice creation for completed migrated
 * appointments; this tool is the manual override for exactly that case, so
 * applying it here would disable the tool on the only appointments it serves.
 */
function blockedReason(appointment: WithRef<IAppointment>): string | undefined {
  if (!Firestore.isCreatedByMigration(appointment)) {
    return 'Invoices can only be generated for migrated appointments';
  }
  if (appointment.invoiceRef) {
    return 'This appointment already has an invoice';
  }
  return;
}

@Injectable({ providedIn: 'root' })
export class GenerateInvoiceForAppointmentTool implements IManagementTool {
  private _snackBar = inject(MatSnackBar);
  private _organisationService = inject(OrganisationService);

  readonly name = 'generate-invoice-for-appointment';
  readonly label = 'Generate Invoice';
  readonly icon = 'receipt_long';
  readonly description =
    'Generate an invoice for a migrated appointment that does not yet have one';
  readonly resource = ManagementResource.Appointment;

  isDisabled(context: IToolRunContext): Observable<boolean> {
    if (!context.resource) {
      return of(true);
    }
    return context.resource.value$.pipe(
      map((value) => {
        if (!value || !isAppointmentWithRef(value)) {
          return true;
        }
        return blockedReason(value) !== undefined;
      })
    );
  }

  async run(context: IToolRunContext): Promise<void> {
    if (!context.resource) {
      return;
    }
    const value = await snapshot(context.resource.value$);
    if (!value || !isAppointmentWithRef(value)) {
      return;
    }
    const blocked = blockedReason(value);
    if (blocked) {
      this._snackBar.open(blocked, 'OK', { duration: 5000 });
      return;
    }
    try {
      const taxRate = await snapshot(this._organisationService.taxRate$);
      if (!taxRate) {
        this._snackBar.open('Could not determine tax rate', 'OK', {
          duration: 5000,
        });
        return;
      }
      await createInvoiceForAppointment(value.ref, taxRate);
      this._snackBar.open('Invoice generated successfully', 'OK', {
        duration: 3000,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this._snackBar.open(`Failed to generate invoice: ${message}`, 'OK', {
        duration: 5000,
      });
    }
  }
}
